import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Menu, X, User, LogOut } from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Button } from "../components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ai-garment-try-on/artifacts/skm3z4j3_Photoroom_20251125_232246.jpeg";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="nav-wrapper" data-testid="navbar">
      <div className="container-custom">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src={LOGO_URL} alt="TailorGlam" className="w-10 h-10 object-contain" />
            <span className="font-heading text-xl md:text-2xl font-medium tracking-tight text-white">
              TailorGlam
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/gallery" className="nav-link" data-testid="nav-gallery">
              Gallery
            </Link>
            <Link to="/style-guide" className="nav-link" data-testid="nav-style-guide">
              Style Guide
            </Link>
            <Link to="/subscription" className="nav-link" data-testid="nav-pricing">
              Pricing
            </Link>
            {user ? (
              <>
                <Link to="/studio" className="nav-link" data-testid="nav-studio">
                  Studio
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 rounded-none text-white hover:bg-white/10" data-testid="user-menu-trigger">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-[#FF1493] flex items-center justify-center text-white text-sm">
                          {user.name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className="text-sm font-medium">{user.name?.split(' ')[0]}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 rounded-none bg-[#141414] border-white/10">
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer text-white" data-testid="menu-dashboard">
                        <User className="w-4 h-4" />
                        Dashboard
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-[#FF1493]" data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-4">
                <Link to="/login" data-testid="nav-login">
                  <Button variant="ghost" className="rounded-none text-white hover:bg-white/10">Login</Button>
                </Link>
                <Link to="/signup" data-testid="nav-signup">
                  <button className="btn-primary">Get Started</button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              <Link to="/gallery" className="nav-link py-2" onClick={() => setMobileMenuOpen(false)}>
                Gallery
              </Link>
              <Link to="/style-guide" className="nav-link py-2" onClick={() => setMobileMenuOpen(false)}>
                Style Guide
              </Link>
              <Link to="/subscription" className="nav-link py-2" onClick={() => setMobileMenuOpen(false)}>
                Pricing
              </Link>
              {user ? (
                <>
                  <Link to="/studio" className="nav-link py-2" onClick={() => setMobileMenuOpen(false)}>
                    Studio
                  </Link>
                  <Link to="/dashboard" className="nav-link py-2" onClick={() => setMobileMenuOpen(false)}>
                    Dashboard
                  </Link>
                  <button onClick={handleLogout} className="nav-link py-2 text-left text-[#FF1493]">
                    Logout
                  </button>
                </>
              ) : (
                <div className="flex flex-col gap-2 pt-4 border-t border-white/10">
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full rounded-none text-white">Login</Button>
                  </Link>
                  <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>
                    <button className="btn-primary w-full">Get Started</button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
